import {
  ActionIcon,
  Button,
  Divider,
  Flex,
  NumberInput,
  Select,
  Stack,
  Text,
  Tooltip
} from '@mantine/core';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from '../NewOrder.module.css';
import { useContext, useMemo } from 'react';
import { ICashierBox, IStore } from '../../../interfaces/Store';
import { IconArrowLeft } from '@tabler/icons-react';
import { formatMoneyString } from '../../../utils/strings';
import PaymentConfigEnum from '../../../enum/PaymentConfig';
import { OrderContext } from '../../../context/OrderContext';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import useCanUseWhatsappNotifications from '../../../hooks/useCanUseWhatsappNotifications';
import { isWhatsappNotificationMissingCustomerPhone } from '../utils/whatsappNotifications';
import { resolveOrderDisplayTotals } from '../utils/orderTotals';

const OrderTotalSummary = ({
  cashierboxes,
  store,
  setShowOrderTotalSummary,
  resetOrder
}: {
  cashierboxes: ICashierBox[];
  store: IStore | null;
  setShowOrderTotalSummary: (show: boolean) => void;
  resetOrder: () => void;
}) => {
  const { t } = useTranslation(['NewOrder', 'FieldName']);
  const { watch, setValue, getValues, clearErrors } = useFormContext();
  const total = watch('total');
  const totalDiscountAmount = watch('totalDiscountAmount');
  const taxesTotal = watch('taxesTotal');
  const shippingServiceTotal = watch('shippingServiceTotal');
  const loyaltyDiscountAmount = watch('loyaltyDiscountAmount');
  const productTotalWithoutDiscount = watch('productTotalWithoutDiscount');
  const subtotal = watch('subtotal');
  const displayTotals = watch('displayTotals');
  const { isUpdatingOrder } = useContext(OrderContext);
  const canUseWhatsappNotifications = useCanUseWhatsappNotifications();
  const whatsappMissingPhoneOptions = {
    blockNewOrdersIfWhatsappCustomerPhoneMissing:
      store?.orderPageConfig?.blockNewOrdersIfWhatsappCustomerPhoneMissing ?? true,
    isUpdatingOrder,
  };

  const openWhatsappMissingCustomerPhoneModal = () => {
    ConfirmationModal({
      title: t('NewOrder:whatsappCustomerPhoneRequiredTitle'),
      children: <Text>{t('NewOrder:whatsappCustomerPhoneRequiredMessage')}</Text>,
      labels: {
        confirm: t('NewOrder:whatsappCustomerPhoneRequiredConfirm'),
        cancel: ''
      },
      cancelProps: {
        style: {
          display: 'none'
        }
      },
      onConfirm: () => {},
      onCancel: () => {}
    });
  };

  const handleNotifyByChange = (value: string | null) => {
    const nextNotifyBy = value || '';
    setValue('notifyBy', nextNotifyBy);

    if (
      isWhatsappNotificationMissingCustomerPhone({
        ...getValues(),
        notifyBy: nextNotifyBy
      }, whatsappMissingPhoneOptions)
    ) {
      openWhatsappMissingCustomerPhoneModal();
      return;
    }

    clearErrors('notifyBy');
  };

  const notificationsMethods = useMemo(() => {
    const baseNotificationsMethods = [
      { value: 'none', label: t('FieldName:notifyBy.none') },
      { value: 'email', label: t('FieldName:notifyBy.email') },
      { value: 'sms', label: t('FieldName:notifyBy.sms') }
    ];
    if (canUseWhatsappNotifications) {
      baseNotificationsMethods.push({ value: 'whatsapp', label: t('FieldName:notifyBy.whatsapp') });
    }
    return baseNotificationsMethods;
  }, [canUseWhatsappNotifications]);

  const { subtotalDisplay, taxesLabelKey } = resolveOrderDisplayTotals(
    { displayTotals, productTotalWithoutDiscount, subtotal },
    store
  );

  return (
    <Stack className={styles.pickupContent} gap={'xs'}>
      <Flex justify={'space-between'} direction={'column'}>
        <ActionIcon
          variant="subtle"
          color="#14a1e6"
          onClick={() => setShowOrderTotalSummary(false)}>
          <IconArrowLeft size={16} />
        </ActionIcon>
        <Divider />
      </Flex>
      <Stack w={'100%'}>
        {!isUpdatingOrder && (
          <Select
            defaultValue={getValues('cashierBox')}
            allowDeselect={false}
            label={t('FieldName:cashierBox')}
            placeholder={t('FieldName:cashierBox')}
            data={(cashierboxes || []).map((cashierbox: ICashierBox) => {
              return {
                label: cashierbox.name,
                value: cashierbox._id,
                key: cashierbox._id
              };
            })}
            onChange={(value: string | null) => {
              setValue('cashierBox', value);
            }}
          />
        )}
        <Select
          allowDeselect={false}
          defaultValue={getValues('notifyBy')}
          placeholder={t('FieldName:notifyByMethod')}
          label={t('FieldName:notifyByMethod')}
          data={notificationsMethods}
          onChange={handleNotifyByChange}
        />

        <Stack gap={'0'}>
          <Flex justify={'space-between'}>
            <Tooltip label={t('NewOrder:customerDiscount')}>
              <Text lineClamp={1}>{t('NewOrder:customerDiscount')}</Text>
            </Tooltip>
            {/* Note: discount code prevents customer discount to be applied */}
            <Text lineClamp={1}>
              {((getValues('discountCode') ? 0 : getValues('customerInfo.discount') || 0) / 100) *
                100}
              %
            </Text>
          </Flex>
          <Flex justify={'space-between'} direction={'column'}>
            <Tooltip label={t('NewOrder:appliedDiscounts')}>
              <Text lineClamp={1}>{t('NewOrder:appliedDiscounts')}</Text>
            </Tooltip>
            {/* Note: discount code prevents customer discount to be applied */}
            <ul>
              {getValues('discountsToApply').map((discount: any) => (
                <li key={discount._id}>
                  {discount.name} - {discount.value}%
                </li>
              ))}
            </ul>
          </Flex>
          {store?.taxOne && store?.taxOne?.name && store?.taxOne?.value ? (
            <Flex justify={'space-between'}>
              <Text lineClamp={1}>{store.taxOne.name}</Text>
              <Text lineClamp={1}>{(store.taxOne.value / 100) * 100}%</Text>
            </Flex>
          ) : null}
          {store?.taxTwo && store?.taxTwo?.name && store?.taxTwo?.value ? (
            <Flex justify={'space-between'}>
              <Text lineClamp={1}>{store.taxTwo.name}</Text>
              <Text lineClamp={1}>{(store.taxTwo.value / 100) * 100}%</Text>
            </Flex>
          ) : null}
          {store?.taxThree && store?.taxThree?.name && store?.taxThree?.value ? (
            <Flex justify={'space-between'}>
              <Text lineClamp={1}>{store.taxThree.name}</Text>
              <Text lineClamp={1}>{(store.taxThree.value / 100) * 100}%</Text>
            </Flex>
          ) : null}
        </Stack>
        <Divider />
        <Stack gap={'0'}>
          <Flex justify={'space-between'}>
            <Text lineClamp={1}>{t('FieldName:subtotal')}</Text>
            <Text lineClamp={1}>{formatMoneyString((subtotalDisplay || 0).toFixed(2))}</Text>
          </Flex>
          <Flex justify={'space-between'}>
            <Text lineClamp={1}>{t('FieldName:discount')}</Text>
            <Text lineClamp={1}>
              {formatMoneyString((totalDiscountAmount || 0).toFixed(2))}
            </Text>
          </Flex>
          <Flex justify={'space-between'}>
            <Text lineClamp={1}>{t(`FieldName:${taxesLabelKey}`)}</Text>
            <Text lineClamp={1}>
              {formatMoneyString((taxesTotal || 0).toFixed(2))}
            </Text>
          </Flex>
          <Flex justify={'space-between'}>
            <Text lineClamp={1}>{t('FieldName:shippingService')}</Text>
            <Text lineClamp={1}>
              {formatMoneyString((shippingServiceTotal || 0).toFixed(2))}
            </Text>
          </Flex>
          <Flex justify={'space-between'}>
            <Text lineClamp={1}>{t('FieldName:credit')}</Text>
            <Text lineClamp={1}>
              {formatMoneyString((getValues('customerInfo.credit') || 0).toFixed(2))}
            </Text>
          </Flex>
          <Flex justify={'space-between'}>
            <Text lineClamp={1}>{t('FieldName:loyaltyDiscountAmount')}</Text>
            <Text lineClamp={1}>
              {formatMoneyString((loyaltyDiscountAmount || 0).toFixed(2))}
            </Text>
          </Flex>
          <Flex justify={'space-between'}>
            <Text lineClamp={1}>{t('FieldName:total')}</Text>
            <Text lineClamp={1} fw={'bold'}>
              {formatMoneyString((total || 0).toFixed(2))}
            </Text>
          </Flex>
        </Stack>
        <Divider />
        {!isUpdatingOrder ? (
          <>
            <Flex gap={'xs'}>
              <Select
                flex={1}
                allowDeselect
                clearable
                defaultValue={getValues('prepaidPaymentMethod')}
                placeholder={t('FieldName:prepaidPaymentMethod')}
                label={t('FieldName:prepaidPaymentMethod')}
                onChange={(value: string | null) => {
                  setValue('prepaidPaymentMethod', value);
                  if (value) {
                    // CUSTOMER WILL PAY THE REST ON DELIVERY
                    setValue('paymentMethod', PaymentConfigEnum.DELIVERY);
                  }
                }}
                data={Object.keys(PaymentConfigEnum)
                  .filter((key) => key.toLowerCase() !== PaymentConfigEnum.DELIVERY.toLowerCase())
                  .map((key) => {
                    const paymentConfigKey = key as keyof typeof PaymentConfigEnum;
                    return {
                      value: PaymentConfigEnum[paymentConfigKey],
                      label: t(`FieldName:paymentMethods.${PaymentConfigEnum[paymentConfigKey]}`)
                    };
                  })}
              />
              <NumberInput
                flex={1}
                defaultValue={getValues('prepaidAmount')}
                placeholder={t('FieldName:prepaidAmount')}
                label={t('FieldName:prepaidAmount')}
                allowLeadingZeros={false}
                allowNegative={false}
                allowDecimal={true}
                onChange={(value: string | number) => {
                  setValue('prepaidAmount', +value);
                }}
              />
            </Flex>
          </>
        ) : null}

        {!isUpdatingOrder && (
          <Button
            variant="outline"
            color="#14a1e6"
            onClick={() => {
              ConfirmationModal({
                children: <Text>{t('NewOrder:restartOrderConfirmation')}</Text>,
                onCancel: () => {},
                onConfirm: () => {
                  resetOrder();
                }
              });
            }}>
            {t('NewOrder:restartOrder')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default OrderTotalSummary;
